import React, { useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCatalogue } from "../../../../hooks/useCatalogue";
import "../../Results/level.css";
function Table({ students }) {
  const { level, semester } = useParams();
  const navigate = useNavigate();
  const { getCoursesForLevel, isProfessional } = useCatalogue();

  const {
    professional_courses,
    completeStudents,
    incompleteStudents,
    externals,
  } = useMemo(() => {
    if (!students?.length)
      return {
        professional_courses: [],
        completeStudents: [],
        incompleteStudents: [],
        externals: [],
      };

    const current_semester = getCoursesForLevel(level, semester);
    const course_codes = Object.keys(current_semester);
    const professional_courses = course_codes.filter((code) =>
      isProfessional(code),
    );
    const courseCodesSet = new Set(course_codes);

    const completeStudents = [];
    const incompleteStudents = [];
    const externals = [];

    const sortedStudents = [...students].sort((a, b) =>
      a.fullname.localeCompare(b.fullname),
    );

    sortedStudents.forEach((student) => {
      const studentCourseSet = new Set(
        student.courses.map((c) => c.course_code),
      );
      const hasAll = professional_courses.every((code) =>
        studentCourseSet.has(code),
      );

      // correctly detect external courses by checking against catalogue set
      const externalCourses = student.courses.filter(
        (course) => !courseCodesSet.has(course.course_code),
      );

      if (externalCourses.length > 0) {
        externals.push({
          _id: student.student_id,
          fullname: student.fullname,
          reg_no: student.reg_no,
          courses: externalCourses,
        });
      }

      if (hasAll) {
        completeStudents.push(student);
      } else {
        incompleteStudents.push(student);
      }
    });

    return {
      professional_courses,
      completeStudents,
      incompleteStudents,
      externals,
    };
  }, [students, level, semester, getCoursesForLevel, isProfessional]);

  const gradeLabels = useMemo(() => ["F", "E", "D", "C", "B", "A"], []);

  const handleStudentClick = useCallback(
    (studentId) => navigate(`/admin/student/${studentId}`),
    [navigate],
  );

  const renderResultCell = useCallback(
    (student, code) => {
      const course = student.courses.find((c) => c.course_code === code);
      if (!course)
        return (
          <td key={code} style={{ textAlign: "center" }}>
            —
          </td>
        );
      const gradeText = gradeLabels[course.grade];
      const isFail = gradeText === "F" || Number(course.grade) === 0;
      return (
        <td key={code} className="center results_course_col">
          <div className="results_course_cell">
            <span className="results_course_total">
              {Number(course.total).toFixed(0)}
            </span>
            <span className={`results_course_grade ${isFail ? "is_fail" : ""}`}>
              {gradeText}
            </span>
          </div>
        </td>
      );
    },
    [gradeLabels],
  );

  return (
    <div className="tabl results_table_root" id="myTable">
      {/* ── Complete Results ── */}
      {completeStudents.length > 0 && (
        <section className="results_table_block results_table_block_plain">
          <div className="results_table_scroll results_table_scroll_main">
            <table className="results_main_table">
              <thead>
                <tr>
                  <th className="center results_col_sn">S/N</th>
                  <th className="left results_col_name">Names</th>
                  <th className="results_col_reg">Reg. No</th>
                  {professional_courses.map((code) => (
                    <th key={code} className="center results_course_col">
                      {code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {completeStudents.map((student, i) => (
                  <tr key={student.student_id || i}>
                    <td className="center results_col_sn">{i + 1}</td>
                    <td
                      onClick={() => handleStudentClick(student.student_id)}
                      className="results_clickable_cell results_name_cell results_col_name"
                    >
                      {student.fullname}
                    </td>
                    <td
                      onClick={() => handleStudentClick(student.student_id)}
                      className="results_clickable_cell results_reg_cell results_col_reg"
                    >
                      {student.reg_no}
                    </td>
                    {professional_courses.map((code) =>
                      renderResultCell(student, code),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Incomplete Results ── */}
      {incompleteStudents.length > 0 && (
        <section className="results_table_block results_table_block_plain">
          <h3 className="results_section_title page_break">
            Incomplete Results
          </h3>
          <div className="results_table_scroll results_table_scroll_main">
            <table className="results_main_table">
              <thead>
                <tr>
                  <th className="center results_col_sn">S/N</th>
                  <th className="left results_col_name">Names</th>
                  <th className="results_col_reg">Reg. No</th>
                  {professional_courses.map((code) => (
                    <th key={code} className="center results_course_col">
                      {code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {incompleteStudents.map((student, i) => (
                  <tr key={student.student_id || i}>
                    <td className="center results_col_sn">{i + 1}</td>
                    <td
                      onClick={() => handleStudentClick(student.student_id)}
                      className="results_clickable_cell results_name_cell results_col_name"
                    >
                      {student.fullname}
                    </td>
                    <td
                      onClick={() => handleStudentClick(student.student_id)}
                      className="results_clickable_cell results_reg_cell results_col_reg"
                    >
                      {student.reg_no}
                    </td>
                    {professional_courses.map((code) =>
                      renderResultCell(student, code),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Other Courses Results ── */}
      {externals.length > 0 && (
        <section className="results_table_block results_table_block_plain">
          <h3 className="results_section_title page_break">
            Other Courses Results
          </h3>
          <div className="results_table_scroll">
            <table className="results_external_table carryover_table">
              <thead>
                <tr>
                  <th className="center carry_col_sn">S/N</th>
                  <th className="carry_col_name">Names</th>
                  <th className="carry_col_reg">Reg. No</th>
                  <th className="carry_col_courses">Courses</th>
                </tr>
              </thead>
              <tbody>
                {externals.map(({ _id, fullname, reg_no, courses }, i) => (
                  <tr key={_id || reg_no || i}>
                    <td className="center carry_col_sn">{i + 1}</td>
                    <td className="results_name_cell carry_col_name">
                      {fullname}
                    </td>
                    <td className="results_reg_cell carry_col_reg">{reg_no}</td>
                    <td className="carry_col_courses">
                      <div className="results_course_list">
                        {courses.map((course) => {
                          const gradeText = gradeLabels[course.grade];
                          const isFail =
                            gradeText === "F" || Number(course.grade) === 0;
                          return (
                            <div
                              key={course.course_code}
                              className="results_course_list_item"
                            >
                              <span className="carry_course_code">
                                {course.course_code}
                              </span>
                              <span className="results_course_total carry_course_total">
                                {Number(course.total).toFixed()}
                              </span>
                              <span
                                className={`results_course_grade carry_course_grade ${isFail ? "is_fail" : ""}`}
                              >
                                {gradeText}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

export default React.memo(Table);
